// Brand / Moodboard — visual port of Inku's brand surface. Stubbed
// gallery: products, lifestyles, palettes, typography. No real upload
// pipeline; "Add" actions show a "demo only" toast.

import { Palette, Plus, Sparkles, Type as TypeIcon } from "lucide-react";
import { useState } from "react";

import { Lightbox } from "@/components/Lightbox";
import { Button } from "@/components/ui/button";
import { useStore } from "@/lib/store";

interface MoodboardItem {
  id: string;
  label: string;
  thumb: string;
}

const PRODUCTS: MoodboardItem[] = [
  { id: "p1", label: "BRAME 03 · Aperitif", thumb: "linear-gradient(160deg, #6E2A14 0%, #C7935E 70%, #F3EADB 100%)" },
  { id: "p2", label: "BRAME 03 · Citrus", thumb: "linear-gradient(160deg, #FF8FA0 0%, #FFD08A 70%, #F3EADB 100%)" },
  { id: "p3", label: "Loop Foods · Bowl", thumb: "linear-gradient(160deg, #2E7D32 0%, #76C893 70%, #F2EBC1 100%)" },
  { id: "p4", label: "Loop Foods · Spread", thumb: "linear-gradient(160deg, #FFB17A 0%, #C7935E 70%, #F2EBC1 100%)" },
  { id: "p5", label: "Veldt · Bottle 750ml", thumb: "linear-gradient(160deg, #6B4FFF 0%, #1A007F 100%)" },
  { id: "p6", label: "Veldt · Limited Edition", thumb: "linear-gradient(160deg, #B5A4FF 0%, #5A2BFF 70%, #1A007F 100%)" },
];

const LIFESTYLES: MoodboardItem[] = [
  { id: "l1", label: "Quiet Sunday · slow morning", thumb: "linear-gradient(135deg, #FFB17A 0%, #FFD08A 80%, #F3EADB 100%)" },
  { id: "l2", label: "Studio · warm light", thumb: "linear-gradient(135deg, #C7935E 0%, #FFB17A 80%, #F2EBC1 100%)" },
  { id: "l3", label: "Roof terrace · golden hour", thumb: "linear-gradient(135deg, #FF8FA0 0%, #FFD08A 80%, #FFE5D2 100%)" },
  { id: "l4", label: "Cocktail · low key", thumb: "linear-gradient(135deg, #1A007F 0%, #6B4FFF 60%, #B5A4FF 100%)" },
  { id: "l5", label: "Concrete kitchen", thumb: "linear-gradient(135deg, #6E6788 0%, #A1A1AA 70%, #FAFAF7 100%)" },
  { id: "l6", label: "Garden, late afternoon", thumb: "linear-gradient(135deg, #2E7D32 0%, #76C893 70%, #FFD08A 100%)" },
];

const PALETTES = [
  { id: "pal1", name: "BRAME · house", colors: ["#6E2A14", "#C7935E", "#F3EADB", "#3502F6"] },
  { id: "pal2", name: "Loop · seasonal", colors: ["#2E7D32", "#76C893", "#FFD08A", "#F2EBC1"] },
  { id: "pal3", name: "Veldt · evening", colors: ["#1A007F", "#6B4FFF", "#B5A4FF", "#FFC8DD"] },
];

const TYPE_PAIRINGS = [
  { id: "t1", name: "Editorial classic", display: "Fraunces", body: "Inter Tight" },
  { id: "t2", name: "Modern brutalist", display: "Space Grotesk", body: "JetBrains Mono" },
  { id: "t3", name: "Studio elegant", display: "FreightDisp", body: "Inter Tight" },
];

export function BrandPage() {
  const { setToast } = useStore();
  const tellDemoOnly = () =>
    setToast({ kind: "ok", text: "Asset uploads are not wired up in this demo." });

  // Lightbox state — clicking any product or lifestyle tile opens it.
  const [preview, setPreview] = useState<{
    item: MoodboardItem;
    section: "Product" | "Lifestyle";
  } | null>(null);

  return (
    <div className="mx-auto w-full max-w-[1400px] px-6 md:px-10 py-12 space-y-12">
      {/* Header */}
      <div className="space-y-2">
        <p className="text-tiny tracking-[0.18em] uppercase text-muted-foreground">
          Workspace
        </p>
        <h1 className="font-freight text-5xl font-light tracking-tight leading-none">
          Brand
        </h1>
        <p className="text-muted-foreground max-w-xl">
          The agent's source of truth: products, lifestyle directions,
          palettes, typography. Everything generated stays in this voice.
        </p>
      </div>

      {/* Products */}
      <Section title="Products" icon={Sparkles} count={PRODUCTS.length} onAdd={tellDemoOnly}>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {PRODUCTS.map((p) => (
            <MoodTile
              key={p.id}
              item={p}
              ratio="aspect-[3/4]"
              onClick={() => setPreview({ item: p, section: "Product" })}
            />
          ))}
        </div>
      </Section>

      {/* Lifestyles */}
      <Section title="Lifestyles" icon={Sparkles} count={LIFESTYLES.length} onAdd={tellDemoOnly}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {LIFESTYLES.map((l) => (
            <MoodTile
              key={l.id}
              item={l}
              ratio="aspect-[4/3]"
              onClick={() => setPreview({ item: l, section: "Lifestyle" })}
            />
          ))}
        </div>
      </Section>

      {/* Palettes */}
      <Section title="Palettes" icon={Palette} count={PALETTES.length} onAdd={tellDemoOnly}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {PALETTES.map((p) => (
            <div key={p.id} className="rounded-lg border border-border bg-card p-4 shadow-card">
              <p className="text-tiny tracking-[0.18em] uppercase text-muted-foreground mb-3">
                {p.name}
              </p>
              <div className="flex gap-1.5">
                {p.colors.map((c) => (
                  <div
                    key={c}
                    className="h-12 flex-1 rounded ring-1 ring-border/50"
                    style={{ backgroundColor: c }}
                    title={c}
                  />
                ))}
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5 text-[10px] font-mono text-muted-foreground/70">
                {p.colors.map((c) => (
                  <span key={c}>{c}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Typography */}
      <Section title="Typography" icon={TypeIcon} count={TYPE_PAIRINGS.length} onAdd={tellDemoOnly}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {TYPE_PAIRINGS.map((t) => (
            <div key={t.id} className="rounded-lg border border-border bg-card p-5 shadow-card">
              <p className="text-tiny tracking-[0.18em] uppercase text-muted-foreground mb-3">
                {t.name}
              </p>
              <p className="font-freight text-3xl font-light leading-tight tracking-tight">
                Aa
              </p>
              <div className="mt-3 space-y-1">
                <p className="text-xs text-muted-foreground">
                  Display · <span className="text-foreground">{t.display}</span>
                </p>
                <p className="text-xs text-muted-foreground">
                  Body · <span className="text-foreground">{t.body}</span>
                </p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Lightbox
        open={preview !== null}
        onClose={() => setPreview(null)}
        title={preview?.item.label ?? ""}
        subtitle={preview?.section}
        thumb={preview?.item.thumb ?? ""}
        meta={
          preview
            ? [
                { label: "Type", value: preview.section },
                { label: "Status", value: "Approved" },
                { label: "Used in", value: "3 canvases" },
                { label: "Variants", value: "12" },
              ]
            : []
        }
      />
    </div>
  );
}

function Section({
  title,
  icon: Icon,
  count,
  onAdd,
  children,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  count: number;
  onAdd: () => void;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Icon className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-freight text-2xl font-light tracking-tight">
            {title}
          </h2>
          <span className="font-mono text-xs text-muted-foreground">
            {String(count).padStart(2, "0")}
          </span>
        </div>
        <Button onClick={onAdd} variant="ghost" size="sm" className="gap-1.5">
          <Plus className="h-3.5 w-3.5" />
          Add
        </Button>
      </div>
      {children}
    </section>
  );
}

function MoodTile({
  item,
  ratio,
  onClick,
}: {
  item: MoodboardItem;
  ratio: string;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="text-left rounded-lg overflow-hidden ring-1 ring-border bg-card group cursor-pointer hover:ring-foreground/20 transition-all"
    >
      <div className={`${ratio} w-full relative`} style={{ background: item.thumb }}>
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
      <div className="px-3 py-2">
        <p className="text-xs text-foreground line-clamp-1">{item.label}</p>
      </div>
    </button>
  );
}
